import { Fragment, useContext, useRef, useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import { Dialog, Transition } from "@headlessui/react";
import { WithContext as ReactTags } from "react-tag-input";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Select from "react-select";
import toast from "react-hot-toast";
import { privateAxios } from "../utils/axios";
import AsyncSelect from "react-select/async";
const KeyCodes = {
  comma: 188,
  enter: 13,
  backspace: 8,
};
const delimiters = [KeyCodes.comma, KeyCodes.enter];

export default function AddExpenseDetails({
  addExpenseModalSetting,
  handlePageUpdate,
  authContext,
}) {
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [products, setProducts] = useState([]);
  const [allProductsRaw, setAllProductsRaw] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const cancelButtonRef = useRef(null);

  // Add refs for form fields
  const branchNameRef = useRef(null);
  const branchNumberRef = useRef(null);
  const receiverNameRef = useRef(null);
  const receiverNumberRef = useRef(null);
  const refNoRef = useRef(null);

  const nextRowId = useRef(1);

  const [expense, setExpense] = useState({
    branchName: "",
    branchNumber: "",
    receiverName: "",
    receiverPhone: "",
    ref: "",
    note: "",
    expenseDate: new Date().toISOString().split("T")[0],
    products: [
      {
        _rowId: 0,
        product: "",
        selectedCategory: "",
        quantity: 1,
        price: 0,
        skus: [],
        warrantyExpireDate: "",
      },
    ],
    totalAmount: 0,
    subTotal: 0,
  });

  useEffect(() => {
    // Fetch products
    privateAxios
      .get("/products/all")
      .then((response) => {
        if (response.data.success && response.data.data) {
          setAllProductsRaw(response.data.data);
          setProducts(
            response.data.data.map((prod) => ({
              value: prod._id,
              label: `${prod.name} - ${prod.category} (${prod.unit})`,
              price: prod.price,
              category: prod.category,
              warranty:
                prod.warranty?.duration === 0
                  ? "0"
                  : `${prod.warranty?.duration || 0} ${
                      prod.warranty?.unit || "days"
                    }`,
            })),
          );
        }
      })
      .catch((err) => {
        console.error("Error fetching products:", err);
        toast.error("Failed to fetch products");
      });
  }, []);

  const calculateTotals = () => {
    const subTotal = expense.products.reduce((sum, item) => {
      return sum + item.quantity * item.price;
    }, 0);

    setExpense((prev) => ({
      ...prev,
      subTotal: subTotal,
      totalAmount: subTotal,
    }));
  };

  useEffect(() => {
    calculateTotals();
  }, [expense.products]);


  const handleProductChange = (index, field, value) => {


    setExpense((prev) => {
      const updatedProducts = [...prev.products];
      const current = updatedProducts[index];

      // 🚫 Prevent duplicate product selection
      if (field === "product") {
        const isDuplicate = prev.products.some(
          (p, i) => i !== index && p.product === value,
        );

        if (isDuplicate) {
          toast.error("This product is already added");
          return prev; // ⛔ stop update
        }

        const selected = products.find((p) => p.value === value);

        updatedProducts[index] = {
          ...current,
          product: value,
          price: selected?.price || 0,
          skus: [], // reset SKUs on product change
          quantity: 0, // reset quantity
        };
      }
      // 📦 SKU change → auto quantity
      else if (field === "skus") {
        updatedProducts[index] = {
          ...current,
          skus: value,
          quantity: value.length,
        };
      }
      // 🧩 other fields
      else {
        updatedProducts[index] = {
          ...current,
          [field]: value,
        };
      }

      return { ...prev, products: updatedProducts };
    });
  };

  const addProductRow = () => {
    setExpense((p) => ({
      ...p,
      products: [
        ...p.products,
        {
          _rowId: nextRowId.current++,
          product: "",
          selectedCategory: "",
          quantity: 1,
          price: 0,
          skus: [],
          warrantyExpireDate: "",
        },
      ],
    }));
  };

  const removeProductRow = (index) => {
    setExpense((prev) => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index),
    }));
  };

  // Validate single field
  const validateField = (fieldName, value) => {
    let error = null;

    if (fieldName.startsWith("product_")) {
      const index = fieldName.split("_")[1];
      if (!value) {
        error = "Product is required";
      } else if (
        expense.products.some(
          (item, i) => i !== Number(index) && item.product === value,
        )
      ) {
        error = "This product is already selected";
      }
    } else if (fieldName.startsWith("quantity_")) {
      if (!value || value < 1) {
        error = "Quantity must be at least 1";
      }
    } else {
      switch (fieldName) {
        case "branchName":
          if (!value?.trim()) error = "Branch name is required";
          break;
        case "branchNumber":
          if (!value?.trim()) error = "Branch number is required";
          break;
        case "receiverName":
          if (!value?.trim()) error = "Receiver name is required";
          break;
        case "receiverPhone":
          if (!value?.trim()) error = "Receiver number is required";
          break;
        default:
          break;
      }
    }

    return error;
  };

  // Handle field validation and navigation
  const handleKeyDown = (e, currentField, index) => {
    if (e.key === "Enter") {
      e.preventDefault();

      // Validate current field
      let error = null;
      if (currentField === "product") {
        error = validateField(
          `product_${index}`,
          expense.products[index].product,
        );
      } else if (currentField === "quantity") {
        error = validateField(
          `quantity_${index}`,
          expense.products[index].quantity,
        );
      } else {
        error = validateField(currentField, expense[currentField]);
      }

      // Update errors state
      setErrors((prev) => ({
        ...prev,
        [currentField === "product"
          ? `product_${index}`
          : currentField === "quantity"
            ? `quantity_${index}`
            : currentField]: error,
      }));

      // If there's an error, don't move to next field
      if (error) {
        return;
      }

      const fieldOrder = {
        branchName: () => branchNumberRef.current?.focus(),
        branchNumber: () => receiverNameRef.current?.focus(),
        receiverName: () => receiverNumberRef.current?.focus(),
        receiverPhone: () => refNoRef.current?.focus(),
        ref: () => document.querySelector(`[data-product-select="0"]`)?.focus(),
        product: (idx) =>
          document.querySelector(`[data-product-quantity="${idx}"]`)?.focus(),
        quantity: (idx) => {
          const nextProductSelect = document.querySelector(
            `[data-product-select="${idx + 1}"]`,
          );
          if (nextProductSelect) {
            nextProductSelect.focus();
          } else {
            // If no more product rows, focus on note field
            const noteInput = document.querySelector('input[name="note"]');
            if (noteInput) {
              noteInput.focus();
            } else {
              document.querySelector('button[type="submit"]')?.focus();
            }
          }
        },
      };

      const nextField = fieldOrder[currentField];
      if (nextField) {
        nextField(index);
      }
    }
  };

  const validateFields = () => {
    const newErrors = {};

    // Required string fields validation
    if (!expense.branchName?.trim()) {
      newErrors.branchName = "Branch name is required";
    }
    if (!expense.branchNumber?.trim()) {
      newErrors.branchNumber = "Branch number is required";
    }
    if (!expense.receiverName?.trim()) {
      newErrors.receiverName = "Receiver name is required";
    }
    if (!expense.receiverPhone?.trim()) {
      newErrors.receiverPhone = "Receiver number is required";
    }
    if (!expense.expenseDate) {
      newErrors.expenseDate = "Date is required";
    }

    // Check for duplicate products
    const selectedProducts = new Set();
    expense.products.forEach((product, index) => {
      // Required product fields validation
      if (!product.product) {
        newErrors[`product_${index}`] = "Product is required";
      } else {
        // Check for duplicates only if product is selected
        if (selectedProducts.has(product.product)) {
          newErrors[`product_${index}`] = "This product is already selected";
        }
        selectedProducts.add(product.product);
      }

      // Quantity validation
      if (!product.quantity || product.quantity < 1) {
        newErrors[`quantity_${index}`] = "Quantity must be at least 1";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateFields()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...expense,
        products: expense.products.map(({ _rowId, selectedCategory, ...rest }) => rest),
      };
      const response = await privateAxios.post("/expenses/", payload);
      if (response.data.success) {
        toast.success("Expense added successfully!");
        handlePageUpdate();
        addExpenseModalSetting();
      } else {
        toast.error(response.data.message || "Failed to add expense");
      }
    } catch (err) {
      console.error("Error adding expense:", err);
      toast.error("Failed to add expense. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const loadBranchOptions = async (inputValue, callback) => {
    if (!inputValue || inputValue.trim().length < 1) {
      callback([]);
      return;
    }
    try {
      const res = await privateAxios.get("/branches", {
        params: { q: inputValue, limit: 20 },
      });
      const branches = res.data?.data?.branches || [];
      callback(
        branches.map((b) => ({
          value: b._id,
          label: `${b.name} - ${b.receiverName}-${b.receiverNumber}`,
          branch: b,
        })),
      );
    } catch (err) {
      console.error("Error searching branches:", err);
      callback([]);
    }
  };

  const handleBranchSelect = (option) => {
    setSelectedBranch(option);
    if (!option) return;
    const b = option.branch;
    setExpense((prev) => ({
      ...prev,
      branchName: b.name || "",
      branchNumber: b.number || "",
      receiverName: b.receiverName || "",
      receiverPhone: b.receiverNumber || "",
    }));
  };

  const checkSkuExists = async (productId, sku) => {
    try {
      console.log(productId, sku);
      const res = await privateAxios.get("/expenses/stock-check-by-sku", {
        params: { productId, sku },
      });

      return res.data;
    } catch (err) {
      return false; // fail-safe: don't allow add
    }
  };

  const renderError = (fieldName) => {
    if (errors[fieldName]) {
      return <p className="mt-1 text-sm text-red-600">{errors[fieldName]}</p>;
    }
    return null;
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={cancelButtonRef}
        onClose={setOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-60 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-7xl h-[90vh] transform overflow-hidden rounded-2xl bg-white p-8 shadow-2xl transition-all flex flex-col">

                <button
                  type="button"
                  className="absolute right-4 top-4 text-gray-400 hover:text-gray-500 focus:outline-none"
                  onClick={() => addExpenseModalSetting()}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>

                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <div className="w-full">
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title
                        as="h3"
                        className="text-xl font-semibold leading-6 text-gray-900"
                      >
                        IT Parts Expense Entry Form
                      </Dialog.Title>
                      <div className="overflow-auto pr-2 h-[80vh] ">
                        <form
                          className="space-y-4"
                          onSubmit={(e) => {
                            e.preventDefault();
                            handleSubmit();
                          }}
                        >
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Search Branch (optional)
                            </label>
                            <AsyncSelect
                              cacheOptions
                              defaultOptions={false}
                              value={selectedBranch}
                              loadOptions={loadBranchOptions}
                              onChange={handleBranchSelect}
                              isClearable
                              placeholder="Search by branch name, number, code or receiver number..."
                              noOptionsMessage={({ inputValue }) =>
                                inputValue ? "No branches found" : "Type to search branches"
                              }
                              className="react-select-container"
                              classNamePrefix="react-select"
                            />
                            <p className="mt-1 text-xs text-gray-400">
                              Selecting a branch auto-fills Branch Name, Branch Number, Receiver Name and Receiver Number below. You can still edit them manually.
                            </p>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                Branch Name *
                              </label>
                              <input
                                type="text"
                                value={expense.branchName}
                                onChange={(e) =>
                                  setExpense((prev) => ({
                                    ...prev,
                                    branchName: e.target.value,
                                  }))
                                }
                                className={`mt-1 block w-full rounded-md border ${
                                  errors.branchName
                                    ? "border-red-300"
                                    : "border-gray-300"
                                } px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500`}
                                ref={branchNameRef}
                                onKeyDown={(e) => handleKeyDown(e, "branchName")}
                              />
                              {renderError("branchName")}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                Branch Number *
                              </label>
                              <input
                                type="text"
                                value={expense.branchNumber}
                                onChange={(e) =>
                                  setExpense((prev) => ({
                                    ...prev,
                                    branchNumber: e.target.value,
                                  }))
                                }
                                className={`mt-1 block w-full rounded-md border ${
                                  errors.branchNumber
                                    ? "border-red-300"
                                    : "border-gray-300"
                                } px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500`}
                                ref={branchNumberRef}
                                onKeyDown={(e) => handleKeyDown(e, "branchNumber")}
                              />
                              {renderError("branchNumber")}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                Receiver Name *
                              </label>
                              <input
                                type="text"
                                value={expense.receiverName}
                                onChange={(e) =>
                                  setExpense((prev) => ({
                                    ...prev,
                                    receiverName: e.target.value,
                                  }))
                                }
                                className={`mt-1 block w-full rounded-md border ${
                                  errors.receiverName
                                    ? "border-red-300"
                                    : "border-gray-300"
                                } px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500`}
                                ref={receiverNameRef}
                                onKeyDown={(e) => handleKeyDown(e, "receiverName")}
                              />
                              {renderError("receiverName")}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                Receiver Number *
                              </label>
                              <input
                                type="text"
                                value={expense.receiverPhone}
                                onChange={(e) =>
                                  setExpense((prev) => ({
                                    ...prev,
                                    receiverPhone: e.target.value,
                                  }))
                                }
                                className={`mt-1 block w-full rounded-md border ${
                                  errors.receiverPhone
                                    ? "border-red-300"
                                    : "border-gray-300"
                                } px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500`}
                                ref={receiverNumberRef}
                                onKeyDown={(e) => handleKeyDown(e, "receiverPhone")}
                              />
                              {renderError("receiverPhone")}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                Date *
                              </label>
                              <input
                                type="date"
                                value={expense.expenseDate}
                                onChange={(e) =>
                                  setExpense((prev) => ({
                                    ...prev,
                                    expenseDate: e.target.value,
                                  }))
                                }
                                className={`mt-1 block w-full rounded-md border ${
                                  errors.expenseDate
                                    ? "border-red-300"
                                    : "border-gray-300"
                                } px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500`}
                              />
                              {renderError("expenseDate")}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                Ref No
                              </label>
                              <input
                                type="text"
                                value={expense.ref}
                                onChange={(e) =>
                                  setExpense((prev) => ({
                                    ...prev,
                                    ref: e.target.value,
                                  }))
                                }
                                className={`mt-1 block w-full rounded-md border ${
                                  errors.ref
                                    ? "border-red-300"
                                    : "border-gray-300"
                                } px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500`}
                                ref={refNoRef}
                                onKeyDown={(e) => handleKeyDown(e, "ref")}
                              />
                              {renderError("ref")}
                            </div>
                          </div>

                          <div className="space-y-4">
                            <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-600 bg-gray-50 p-2 rounded-xl">
                              <div className="col-span-6">Product</div>
                              <div className="col-span-2">
                                {" "}
                                Warranty Expire Date
                              </div>
                              <div className="col-span-1">Qty</div>
                              <div className="col-span-1">Price</div>
                              <div className="col-span-1">Total</div>
                              <div className="col-span-1">Action</div>
                            </div>

                            {expense.products.map((item, i) => (
                              <div
                                key={item._rowId}
                                className="grid grid-cols-12 gap-2 p-2 border rounded-xl items-center"
                              >
                                <div className="col-span-6 rounded-lg border border-gray-200 bg-white p-3 space-y-3 shadow-sm">
                                  {/* Category Filter */}
                                  <div className="space-y-1">
                                    <label className="text-xs font-medium text-gray-600">
                                      Category
                                    </label>
                                    <Select
                                      options={[
                                        { value: "", label: "All Categories" },
                                        ...Array.from(
                                          new Set(products.map((p) => p.category).filter(Boolean))
                                        ).map((cat) => ({ value: cat, label: cat })),
                                      ]}
                                      placeholder="All Categories"
                                      value={
                                        item.selectedCategory
                                          ? { value: item.selectedCategory, label: item.selectedCategory }
                                          : { value: "", label: "All Categories" }
                                      }
                                      onChange={(s) => {
                                        setExpense((prev) => {
                                          const updated = [...prev.products];
                                          updated[i] = {
                                            ...updated[i],
                                            selectedCategory: s.value,
                                            product: "",
                                            price: 0,
                                            skus: [],
                                            quantity: 0,
                                          };
                                          return { ...prev, products: updated };
                                        });
                                      }}
                                      className="react-select-container"
                                      classNamePrefix="react-select"
                                    />
                                  </div>

                                  {/* Product Select */}
                                  <div className="space-y-1">
                                    <label className="text-xs font-medium text-gray-600">
                                      Product
                                    </label>
                                    <Select
                                      options={
                                        item.selectedCategory
                                          ? products.filter((p) => p.category === item.selectedCategory)
                                          : products
                                      }
                                      placeholder="Select a product"
                                      value={products.find((p) => p.value === item.product) || null}
                                      onChange={(s) =>
                                        handleProductChange(
                                          i,
                                          "product",
                                          s.value,
                                        )
                                      }
                                      className="react-select-container"
                                      classNamePrefix="react-select"
                                    />
                                  </div>

                                  {/* SKU Tags */}
                                  <div className="space-y-1">
                                    <label className="text-xs font-medium text-gray-600">
                                      SKUs
                                    </label>

                                    <div className="rounded-md border border-dashed border-gray-300 px-2 py-1 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition">
                                      <ReactTags
                                        tags={(item.skus || []).map((s) => ({
                                          id: s,
                                          text: s,
                                        }))}
                                        delimiters={delimiters}
                                        handleAddition={async (tag) => {
                                          // Capture stable ID and product before async gap
                                          const rowId = item._rowId;
                                          const productId = item.product;
                                          try {
                                            const { exist, data } =
                                              await checkSkuExists(
                                                productId,
                                                tag.text,
                                              );

                                            if (!exist) {
                                              toast.error("SKU does not exist");
                                              return;
                                            }

                                            // Look up by _rowId, not by index i,
                                            // so a removed row above doesn't shift the target
                                            setExpense((prev) => {
                                              const updatedProducts = [...prev.products];
                                              const idx = updatedProducts.findIndex((p) => p._rowId === rowId);
                                              if (idx === -1) return prev;
                                              const current = updatedProducts[idx];
                                              const newSkus = [...(current.skus || []), tag.text];
                                              updatedProducts[idx] = {
                                                ...current,
                                                skus: newSkus,
                                                quantity: newSkus.length,
                                                price: data.price,
                                                warrantyExpireDate: data.warrantyExpireDate,
                                              };
                                              return { ...prev, products: updatedProducts };
                                            });

                                            toast.success("Added success");
                                          } catch (err) {
                                            console.error(err);
                                            toast.error(
                                              "Product not found in stock",
                                            );
                                          }
                                        }}
                                        handleDelete={(idx) => {
                                          const removedSku = item.skus?.[idx];

                                          const updatedSkus = (
                                            item.skus || []
                                          ).filter((_, x) => x !== idx);

                                          handleProductChange(
                                            i,
                                            "skus",
                                            updatedSkus,
                                          );

                                          if (removedSku) {
                                            toast.success(
                                              `SKU "${removedSku}" removed`,
                                            );
                                          }
                                        }}
                                        placeholder="Scan or type SKU and press Enter"
                                        inputFieldPosition="bottom"
                                        autocomplete
                                        allowUnique
                                      />
                                    </div>

                                    <p className="text-[11px] text-gray-400">
                                      Tip: Barcode scanner supported
                                    </p>
                                  </div>
                                </div>

                                <div className="col-span-2 h-10 px-2">
                                  {item.warrantyExpireDate ? (

                                     moment(item.warrantyExpireDate).format("DD/MM/YYYY")

                                    // <input
                                    //   type="date"
                                    //   value={item.warrantyExpireDate}
                                    //   readOnly
                                    //   className="border rounded"
                                    // />
                                  ) : (
                                    "No Warranty"
                                  )}
                                </div>

                                <input
                                  type="number"
                                  value={item.skus.length}
                                  readOnly
                                  className="col-span-1 h-10 border rounded px-2"
                                />

                                <input
                                  type="number"
                                  min={0}
                                  value={item.price}
                                  className="col-span-1 h-10 border rounded px-2 bg-gray-50"
                                />

                                <input
                                  readOnly
                                  value={item.quantity * item.price}
                                  className="col-span-1 h-10 border rounded px-2 bg-gray-100"
                                />

                                <button
                                  type="button"
                                  onClick={() => removeProductRow(i)}
                                  className="text-red-600 hover:text-red-800 col-span-12 sm:col-span-1"
                                >
                                  ✕
                                </button>
                              </div>
                            ))}

                            <button
                              type="button"
                              onClick={addProductRow}
                              className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              + Add Product
                            </button>
                          </div>

                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700">
                              Note
                            </label>
                            <input
                              name="note"
                              type="text"
                              value={expense.note}
                              onChange={(e) =>
                                setExpense((prev) => ({
                                  ...prev,
                                  note: e.target.value,
                                }))
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  document
                                    .querySelector('button[type="submit"]')
                                    ?.focus();
                                }
                              }}
                              className={`mt-1 block w-full rounded-md border ${
                                errors.note
                                  ? "border-red-300"
                                  : "border-gray-300"
                              } px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500`}
                            />
                            {renderError("note")}
                          </div>

                          <div className="flex justify-end gap-4">
                            <div className="w-1/3">
                              <label className="block text-sm font-medium text-gray-700">
                                Sub Total
                              </label>
                              <input
                                type="text"
                                value={expense.subTotal}
                                className={`mt-1 block w-full rounded-md border ${
                                  errors.subTotal
                                    ? "border-red-300"
                                    : "border-gray-300"
                                } px-3 py-2 text-sm bg-gray-50 text-right font-medium`}
                                readOnly
                              />
                              {renderError("subTotal")}
                            </div>
                            <div className="w-1/3">
                              <label className="block text-sm font-medium text-gray-700">
                                Total Amount
                              </label>
                              <input
                                type="text"
                                value={expense.totalAmount}
                                className={`mt-1 block w-full rounded-md border ${
                                  errors.totalAmount
                                    ? "border-red-300"
                                    : "border-gray-300"
                                } px-3 py-2 text-sm bg-gray-50 text-right font-medium`}
                                readOnly
                              />
                              {renderError("totalAmount")}
                            </div>
                          </div>

                          <div className="mt-6 flex justify-end gap-3">
                            <button
                              type="button"
                              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                              onClick={() => addExpenseModalSetting()}
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                              disabled={loading}
                            >
                              {loading ? (
                                <span className="flex items-center gap-2">
                                  <svg
                                    className="animate-spin h-4 w-4"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    />
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    />
                                  </svg>
                                  Processing...
                                </span>
                              ) : (
                                "Save Expense"
                              )}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
